import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Button,
  Chip,
  Checkbox,
  Grid,
  Alert,
  IconButton,
  FormControlLabel,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Divider from '@mui/material/Divider';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import HandymanIcon from '@mui/icons-material/Handyman';
import moment from 'moment';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import { StackedDialog } from '../../utilities/ModalStackContext';
import ApprovedChip from '../ApprovedChip';

const EMPTY_ROW = { name: '', vendor: '', cost: '', notes: '' };

// A row counts as empty (silently skipped on submit) when nothing was entered.
const isRowEmpty = (row) =>
  !row.name.trim() && !row.vendor.trim() && !String(row.cost).trim() && !row.notes.trim();

// The Contractor Payment page pays vendors on the 1st (16th–end of last month)
// and the 16th (1st–15th), selecting items by their linked purchase's date.
const payoutLabel = (isoDate) => {
  const m = moment(isoDate, 'YYYY-MM-DD', true);
  if (!m.isValid()) return '';
  const payday = m.date() <= 15 ? m.clone().date(16) : m.clone().add(1, 'month').date(1);
  return `Paid in the ${payday.format('MMM D, YYYY')} contractor pay period`;
};

export default function SubcontractTab({ order, stockNumber, setOrder }) {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [addOpen, setAddOpen] = React.useState(false);
  const [addRows, setAddRows] = React.useState([{ ...EMPTY_ROW }]);
  const [addError, setAddError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [editing, setEditing] = React.useState(null); // the subcontract being edited

  const canEdit = StateManager.canEditSubcontracts();
  const canDelete = StateManager.canDeleteSubcontracts();

  const subcontracts = React.useMemo(() => order.subcontracts || [], [order.subcontracts]);

  // Optimistically reflect a change in the parent order state; the realtime
  // listener in ServiceOrderModal reconciles shortly after.
  const patchLocal = (updater) => {
    setOrder((prev) => ({ ...prev, subcontracts: updater(prev.subcontracts || []) }));
  };

  const filtered = React.useMemo(() => {
    const q = searchQuery.toLowerCase();
    return subcontracts
      .filter(
        (s) =>
          (s.name || '').toLowerCase().includes(q) ||
          (s.vendor || '').toLowerCase().includes(q) ||
          (s.status || '').toLowerCase().includes(q)
      )
      .sort((a, b) => {
        // Declined items are grouped at the bottom; everything else by date desc.
        if (!!a.declined !== !!b.declined) return a.declined ? 1 : -1;
        const da = a.status_time || a.date;
        const db = b.status_time || b.date;
        const ma = da ? moment(da, ['YYYY/MM/DD', 'YYYY-MM-DD']) : moment(0);
        const mb = db ? moment(db, ['YYYY/MM/DD', 'YYYY-MM-DD']) : moment(0);
        return mb.valueOf() - ma.valueOf();
      });
  }, [subcontracts, searchQuery]);

  // Declined items are excluded from accounting totals.
  const totalCost = subcontracts.reduce(
    (sum, s) => sum + (s.declined ? 0 : parseFloat(s.cost) || 0),
    0
  );

  const handleRowChange = (index, field, value) => {
    setAddRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addAnotherRow = () => setAddRows((prev) => [...prev, { ...EMPTY_ROW }]);

  const removeAddRow = (index) => setAddRows((prev) => prev.filter((_, i) => i !== index));

  const openAddDialog = () => {
    setAddRows([{ ...EMPTY_ROW }]);
    setAddError('');
    setAddOpen(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddError('');

    const filledRows = addRows.filter((row) => !isRowEmpty(row));
    if (filledRows.length === 0) {
      setAddError('Add at least one item.');
      return;
    }
    for (let i = 0; i < filledRows.length; i++) {
      if (!filledRows[i].name.trim()) {
        setAddError(`${filledRows.length > 1 ? `Item ${i + 1}: ` : ''}A description is required.`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // Create each subcontract in one batch. Their linked purchases
      // (purchases/SUB-{id}) are created by the syncSubcontractPurchase trigger.
      const db = firebase.firestore();
      const batch = db.batch();
      const now = moment().format('YYYY/MM/DD');
      filledRows.forEach((row) => {
        const ref = db.collection('subcontracts').doc();
        batch.set(ref, {
          name: row.name.trim(),
          vendor: row.vendor.trim(),
          cost: parseFloat(row.cost) || 0,
          notes: row.notes,
          status: 'pending',
          order: stockNumber,
          date: now,
          status_time: now,
        });
      });
      await batch.commit();
      setAddOpen(false);
      StateManager.setAlertAndOpen(
        `Added ${filledRows.length} subcontract item${filledRows.length > 1 ? 's' : ''}`,
        'success'
      );
    } catch (err) {
      console.error('Error adding subcontract:', err);
      setAddError('Failed to add. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusToggle = async (sub) => {
    if (!canEdit) return;
    const newStatus = sub.status === 'complete' ? 'pending' : 'complete';
    const updates = { status: newStatus, status_time: moment().format('YYYY/MM/DD') };
    patchLocal((list) => list.map((s) => (s.id === sub.id ? { ...s, ...updates } : s)));
    try {
      await firebase.firestore().collection('subcontracts').doc(sub.id).update(updates);
    } catch (err) {
      console.error('Error updating subcontract status:', err);
      StateManager.setAlertAndOpen('Error updating status', 'error');
    }
  };

  // The pay-period date lives on the linked purchase, so load it alongside the edit.
  const openEdit = async (sub) => {
    setEditing({ ...sub });
    if (!StateManager.isBackoffice()) return;
    try {
      const snap = await firebase
        .firestore()
        .collection('purchases')
        .doc(sub.purchaseId || `SUB-${sub.id}`)
        .get();
      const raw = (snap.exists && snap.data().date) || sub.date;
      const payDate = raw ? moment(raw, ['YYYY/MM/DD', 'YYYY-MM-DD']).format('YYYY-MM-DD') : '';
      setEditing((p) =>
        p && p.id === sub.id
          ? { ...p, payDate, originalPayDate: payDate, purchaseExists: snap.exists }
          : p
      );
    } catch (err) {
      console.error('Error loading subcontract purchase:', err);
      StateManager.setAlertAndOpen('Could not load the pay period date', 'error');
    }
  };

  const handleEditSave = async () => {
    if (!editing) return;
    const updates = {
      name: (editing.name || '').trim(),
      vendor: (editing.vendor || '').trim(),
      cost: parseFloat(editing.cost) || 0,
      notes: editing.notes || '',
      status: editing.status || 'pending',
      declined: !!editing.declined,
    };
    // Admins can re-point this item to a different service order (SO/stock number).
    let movedOff = false;
    if (StateManager.isAdmin()) {
      const newOrder = (editing.order || '').trim();
      if (newOrder) {
        updates.order = newOrder;
        movedOff = newOrder !== stockNumber;
      }
    }
    // Pay-period date: stored on the subcontract too, so a purchase recreated
    // later (e.g. after un-declining) keeps it.
    const payDateMoment = moment(editing.payDate || '', 'YYYY-MM-DD', true);
    const payDateChanged =
      StateManager.isBackoffice() &&
      payDateMoment.isValid() &&
      editing.payDate !== editing.originalPayDate;
    if (payDateChanged) updates.date = payDateMoment.format('YYYY/MM/DD');
    // If the item was moved to another SO it no longer belongs to this order,
    // so drop it from the current list; otherwise reflect the edits in place.
    patchLocal((list) =>
      movedOff
        ? list.filter((s) => s.id !== editing.id)
        : list.map((s) => (s.id === editing.id ? { ...s, ...updates } : s))
    );
    setEditing(null);
    try {
      await firebase.firestore().collection('subcontracts').doc(editing.id).update(updates);
      // The sync trigger never rewrites a purchase's date, so move it here.
      if (payDateChanged && editing.purchaseExists && !updates.declined) {
        await firebase
          .firestore()
          .collection('purchases')
          .doc(editing.purchaseId || `SUB-${editing.id}`)
          .update({ date: updates.date });
      }
      StateManager.setAlertAndOpen(
        movedOff ? `Moved to SO ${updates.order}` : 'Subcontracted work updated',
        'success'
      );
    } catch (err) {
      console.error('Error updating subcontract:', err);
      StateManager.setAlertAndOpen('Error updating subcontracted work', 'error');
    }
  };

  const handleDeleteOne = async (id) => {
    if (!canDelete) return;
    if (!window.confirm('Delete this subcontracted work item?')) return;
    patchLocal((list) => list.filter((s) => s.id !== id));
    setEditing(null);
    try {
      await firebase.firestore().collection('subcontracts').doc(id).delete();
      StateManager.setAlertAndOpen('Deleted', 'success');
    } catch (err) {
      console.error('Error deleting subcontract:', err);
      StateManager.setAlertAndOpen('Error deleting', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!canDelete) return;
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} subcontracted work item${ids.length > 1 ? 's' : ''}?`)) return;
    patchLocal((list) => list.filter((s) => !ids.includes(s.id)));
    setSelectedIds([]);
    try {
      const batch = firebase.firestore().batch();
      ids.forEach((id) => batch.delete(firebase.firestore().collection('subcontracts').doc(id)));
      await batch.commit();
      StateManager.setAlertAndOpen(`Deleted ${ids.length} item${ids.length > 1 ? 's' : ''}`, 'success');
    } catch (err) {
      console.error('Error bulk deleting subcontracts:', err);
      StateManager.setAlertAndOpen('Error deleting items', 'error');
    }
  };

  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSelectAll = (checked) =>
    setSelectedIds(checked ? filtered.map((s) => s.id) : []);

  const allSelected = filtered.length > 0 && filtered.every((s) => selectedIds.includes(s.id));
  const someSelected = filtered.some((s) => selectedIds.includes(s.id));

  const statusChip = (status) => {
    const complete = status === 'complete';
    const color = complete ? theme.palette.success.main : theme.palette.info.light;
    return (
      <Chip
        icon={complete ? <CheckCircleIcon /> : <PendingIcon />}
        label={complete ? 'Complete' : 'Pending'}
        sx={{
          bgcolor: alpha(color, 0.1),
          color,
          border: `1px solid ${alpha(color, 0.3)}`,
          cursor: canEdit ? 'pointer' : 'default',
          '&:hover': canEdit ? { bgcolor: alpha(color, 0.2) } : {},
          '& .MuiChip-icon': { color: 'inherit' },
        }}
      />
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          fullWidth
          placeholder="Search subcontracted work..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAddDialog}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Add Subcontract
          </Button>
        )}
      </Box>

      {/* Total Cost Summary */}
      <Box
        sx={{
          display: 'flex',
          gap: 3,
          mb: 2,
          px: 2,
          py: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AttachMoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Total Cost:
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            ${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Box>

      {/* Bulk delete actions */}
      {canDelete && selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
            px: 2,
            py: 1,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {selectedIds.length} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={handleBulkDelete}
          >
            Delete Selected
          </Button>
          <Button variant="outlined" size="small" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </Box>
      )}

      {filtered.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} sx={{ py: 4 }}>
          <HandymanIcon sx={{ fontSize: 48, color: theme.palette.grey[400] }} />
          <Typography variant="h6" color="text.secondary">
            No subcontracted work
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No subcontracted work has been added to this service order yet.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {canDelete && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelected && !allSelected}
                      checked={allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      title="Select all"
                    />
                  </TableCell>
                )}
                <TableCell>Description</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((sub) => {
                const dateVal = sub.status_time || sub.date;
                const dateDisplay = dateVal
                  ? moment(dateVal, ['YYYY/MM/DD', 'YYYY-MM-DD']).format('MMM DD, YYYY')
                  : '-';
                return (
                  <TableRow
                    key={sub.id}
                    hover
                    onClick={() => canEdit && openEdit(sub)}
                    sx={{
                      cursor: canEdit ? 'pointer' : 'default',
                      '&:hover': canEdit ? { bgcolor: alpha(theme.palette.primary.main, 0.04) } : {},
                    }}
                  >
                    {canDelete && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.includes(sub.id)}
                          onChange={() => toggleSelect(sub.id)}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            textDecoration: sub.declined ? 'line-through' : 'none',
                            color: sub.declined ? 'text.disabled' : 'text.primary',
                          }}
                        >
                          {sub.name || 'Unnamed'}
                        </Typography>
                        {sub.declined && (
                          <Chip label="Declined" color="error" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                        <ApprovedChip line={sub} />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {sub.vendor || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box
                        onClick={() => handleStatusToggle(sub)}
                        sx={{ display: 'inline-flex' }}
                      >
                        {statusChip(sub.status)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {dateDisplay}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sub.cost ? `$${parseFloat(sub.cost).toFixed(2)}` : '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Dialog */}
      <StackedDialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Subcontracted Work</DialogTitle>
        <form onSubmit={handleAddSubmit}>
          <DialogContent>
            {addRows.map((row, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                {index > 0 && <Divider sx={{ mb: 2 }} />}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Item {index + 1}
                  </Typography>
                  {addRows.length > 1 && (
                    <IconButton size="small" onClick={() => removeAddRow(index)} disabled={submitting} aria-label="Remove item">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      value={row.name}
                      onChange={(e) => handleRowChange(index, 'name', e.target.value)}
                      fullWidth
                      required
                      autoFocus={index === 0}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Vendor"
                      value={row.vendor}
                      onChange={(e) => handleRowChange(index, 'vendor', e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Cost"
                      type="number"
                      value={row.cost}
                      onChange={(e) => handleRowChange(index, 'cost', e.target.value)}
                      fullWidth
                      inputProps={{ min: 0, step: 0.01 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Notes"
                      value={row.notes}
                      onChange={(e) => handleRowChange(index, 'notes', e.target.value)}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </Grid>
              </Box>
            ))}

            <Button startIcon={<AddIcon />} onClick={addAnotherRow} disabled={submitting} sx={{ mt: 1 }}>
              Add another item
            </Button>

            {addError && (
              <Box mt={2}>
                <Alert severity="error">{addError}</Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)} color="inherit" disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </StackedDialog>

      {/* Edit Dialog */}
      <StackedDialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Subcontracted Work</DialogTitle>
        <DialogContent>
          {editing && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <TextField
                  label="Description"
                  value={editing.name || ''}
                  onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vendor"
                  value={editing.vendor || ''}
                  onChange={(e) => setEditing((p) => ({ ...p, vendor: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Cost"
                  type="number"
                  value={editing.cost ?? ''}
                  onChange={(e) => setEditing((p) => ({ ...p, cost: e.target.value }))}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notes"
                  value={editing.notes || ''}
                  onChange={(e) => setEditing((p) => ({ ...p, notes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!editing.declined}
                      onChange={(e) => setEditing((p) => ({ ...p, declined: e.target.checked }))}
                    />
                  }
                  label="Declined (exclude from estimate & accounting)"
                />
              </Grid>
              {StateManager.isBackoffice() && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Pay Period Date"
                    type="date"
                    value={editing.payDate || ''}
                    onChange={(e) => setEditing((p) => ({ ...p, payDate: e.target.value }))}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    helperText={payoutLabel(editing.payDate) || 'Sets the contractor pay period'}
                  />
                </Grid>
              )}
              {StateManager.isAdmin() && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Service Order (SO #)"
                    value={editing.order || ''}
                    onChange={(e) => setEditing((p) => ({ ...p, order: e.target.value }))}
                    fullWidth
                    helperText="Admin only — move this item to a different SO"
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {canDelete ? (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => editing && handleDeleteOne(editing.id)}>
              Delete
            </Button>
          ) : (
            <Box />
          )}
          <Box>
            <Button onClick={() => setEditing(null)} color="inherit">
              Cancel
            </Button>
            <Button variant="contained" onClick={handleEditSave} sx={{ ml: 1 }}>
              Save
            </Button>
          </Box>
        </DialogActions>
      </StackedDialog>
    </Box>
  );
}
